import { forwardRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { AppLaunchResponse } from '@lp/shared';
import { ExternalLink, RotateCw } from 'lucide-react';
import { api, ApiError } from '../../lib/api.js';
import { Alert, Button, Spinner } from '../ui.js';

/**
 * A hosted Python app (Streamlit, Gradio, Dash) in a frame.
 *
 * There is no fixed address to point the frame at: each viewer asks the API
 * for a launch URL, which lives on its own origin (`s-<token>.<apps host>`)
 * and stops working after a few hours. The query is kept for well under that
 * lifetime and not refetched on focus, so switching tabs does not restart the
 * learner's app session.
 *
 * Unlike an uploaded HTML bundle, the frame is allowed same-origin access,
 * forms, popups and downloads. That is safe because its origin is not ours —
 * same-origin here means the app's own origin, which is exactly what
 * Streamlit and Gradio need for their storage and file widgets.
 */
export const HOSTED_APP_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads';

export function useAppLaunch(featureId: string, enabled: boolean) {
  return useQuery({
    queryKey: ['app-launch', featureId],
    queryFn: () =>
      api.post<AppLaunchResponse>(`/interactive/${encodeURIComponent(featureId)}/launch`),
    enabled,
    staleTime: 60 * 60_000,
    gcTime: 60 * 60_000,
    refetchOnWindowFocus: false,
    retry: (count, err) => !(err instanceof ApiError && err.status < 500) && count < 2,
  });
}

export const HostedAppFrame = forwardRef<
  HTMLIFrameElement,
  { featureId: string; title: string; heightPx: number }
>(function HostedAppFrame({ featureId, title, heightPx }, ref) {
  const launch = useAppLaunch(featureId, true);
  const height = { height: `${Math.min(Math.max(heightPx, 160), 2000)}px` };

  if (launch.isPending) {
    return (
      <div
        style={height}
        className="flex w-full items-center justify-center gap-3 rounded-control border border-line bg-surface-2 text-sm text-ink-muted"
      >
        <Spinner label="Opening the app" />
        Opening the app…
      </div>
    );
  }

  if (launch.isError) {
    return (
      <Alert tone="warn" title="The app could not be opened">
        <p>
          {launch.error instanceof ApiError
            ? launch.error.message
            : 'Something went wrong while opening it.'}
        </p>
        <Button
          size="sm"
          className="mt-2"
          icon={<RotateCw size={14} aria-hidden />}
          onClick={() => void launch.refetch()}
        >
          Try again
        </Button>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <iframe
        ref={ref}
        src={launch.data.url}
        title={title || 'Interactive app'}
        sandbox={HOSTED_APP_SANDBOX}
        allow="clipboard-read; clipboard-write; fullscreen"
        referrerPolicy="no-referrer"
        style={height}
        className="w-full rounded-control border border-line bg-surface-2"
      />
      <div className="flex flex-wrap items-center gap-4">
        <a
          href={launch.data.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 text-xs text-brand-text hover:underline"
        >
          <ExternalLink size={13} aria-hidden />
          Open in a new tab
        </a>
        <button
          type="button"
          className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink hover:underline"
          onClick={() => void launch.refetch()}
        >
          <RotateCw size={13} aria-hidden />
          Restart the app
        </button>
      </div>
    </div>
  );
});
